import type { ReactNode } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import type { SeoStrategyRecommendation } from "@/types/seo-strategy";

interface StrategyCardBodyProps {
  recommendation: SeoStrategyRecommendation;
  /** Present only when the grouping is "suggested" and can still be added. */
  onAddGroupingToMasterList?: () => void;
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
      {children}
    </div>
  );
}

/**
 * `decisions/the-strategy-card-is-a-shell-with-an-action-slot.md` decision 3: the body is
 * its own component so a later editing mode (spec B) can swap it out as a sibling rather
 * than threading a mode through every field. Nothing here collapses -- the toggle the old
 * modal card had is gone; every field that has a value is shown outright.
 */
export function StrategyCardBody({ recommendation, onAddGroupingToMasterList }: StrategyCardBodyProps) {
  const grouping = recommendation.thematicGrouping;
  const hasNamedGrouping = grouping !== null && grouping.name.trim() !== "";
  const { questionText } = recommendation;
  const questionIsHidden = questionText !== null && questionText.trim() === "";
  const hasSavedQuestion = questionText !== null && questionText.trim() !== "";
  const hasWhyItMatters = recommendation.whyItMatters !== null && recommendation.whyItMatters.trim() !== "";

  return (
    <div className="grid gap-6 md:grid-cols-2 md:divide-x md:divide-border">
      <section className="space-y-4 md:pr-6">
        <h5 className="text-base font-semibold leading-6 text-foreground">Opportunity</h5>

        {questionIsHidden ? null : (
          <Field label="Question">
            {hasSavedQuestion ? (
              <p className="whitespace-pre-line text-sm text-foreground">{questionText}</p>
            ) : (
              recommendation.sourceQuestions.map((question) => (
                <p key={question} className="text-sm text-foreground">
                  {question}
                </p>
              ))
            )}
          </Field>
        )}

        {recommendation.consolidatedFinding ? (
          <Field label="Gap">
            <p className="text-sm text-foreground">{recommendation.consolidatedFinding}</p>
          </Field>
        ) : null}

        {hasWhyItMatters ? (
          <Field label="Why it matters">
            <p className="text-sm text-foreground">{recommendation.whyItMatters}</p>
          </Field>
        ) : null}

        {recommendation.evidenceSummary ? (
          <Field label="Evidence">
            <p className="text-sm text-foreground">{recommendation.evidenceSummary}</p>
          </Field>
        ) : null}
      </section>

      <section className="space-y-4 md:pl-6">
        <h5 className="text-base font-semibold leading-6 text-foreground">Recommendation</h5>

        <Field label="Field">
          <p className="text-sm text-foreground">{recommendation.field}</p>
        </Field>

        <Field label="Hypothesis">
          <p className="text-sm text-foreground">{recommendation.hypothesis}</p>
        </Field>

        <Field label="Relevant thematic grouping">
          {hasNamedGrouping && grouping ? (
            <div className="flex flex-wrap items-center gap-2">
              <Badge className="border-0 bg-secondary text-foreground">{grouping.name}</Badge>
              {grouping.status === "suggested" ? <Badge variant="outline">Suggested</Badge> : null}
              {onAddGroupingToMasterList ? (
                <Button variant="link" size="sm" className="h-auto p-0" onClick={onAddGroupingToMasterList}>
                  Add to Master List
                </Button>
              ) : null}
            </div>
          ) : (
            <p className="text-sm text-foreground">None</p>
          )}
        </Field>
      </section>
    </div>
  );
}
